<template>
  <Grid
    class="user-grid"
    :pageable="pageable"
    :data-items="users"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :sort="sort"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @sortchange="sortChangeHandler"
    @pagechange="pageChangeHandler"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e: any) => props.onSortchange(e)"
        @filterchange="(e: any) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:EditCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <font-awesome-icon
          v-if="editColumnVisible"
          class="open-edit"
          @click.prevent="openEditModal(dataItem)"
          icon="fa-solid fa-pen-to-square"
        />
      </td>
    </template>
    <template v-slot:AllPlantsCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ dataItem[field] ? "Y" : "N" }}
      </td>
    </template>
    <template v-slot:DeleteCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <font-awesome-icon
          v-if="!dataItem.IsOuAssignedAdmin && deleteColumnVisible"
          class="open-delete"
          @click.prevent="openDeleteModal(dataItem)"
          icon="times"
        />
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnReorderEvent,
  type GridColumnProps,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
} from "@progress/kendo-vue-grid";
import { computed, ref, watchEffect } from "vue";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import type { MemberShipUserPlus } from "@/Domain/MemberShipUserPlus";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { getFilterOperatorsForBoolean } from "@/shared/helpers/filtersHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { useMainStore } from "@/stores/useMainStore";
import { UserRoles } from "@/Domain/UserRoles";

const main = useMainStore();

const props = defineProps<{
  users: MemberShipUserPlus[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<MemberShipUserPlus[]>;
}>();

const emit = defineEmits<{
  (e: "editClicked", transaction: MemberShipUserPlus): void;
  (e: "deleteClicked", transaction: MemberShipUserPlus): void;
  (e: "usersChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const editColumnVisible = computed(() => {
  return (
    main.isInRole(UserRoles.ROLE_EXTERNAL_CUSTOMER_ADMIN) ||
    main.isInRole(UserRoles.ROLE_IN_HOUSE_MIS_MANAGER)
  );
});

const deleteColumnVisible = computed(() => {
  return (
    main.isInRole(UserRoles.ROLE_EXTERNAL_CUSTOMER_ADMIN) ||
    main.isInRole(UserRoles.ROLE_IN_HOUSE_MIS_MANAGER)
  );
});

const columns = ref<GridColumnPropsExtended[]>([
  {
    cell: "EditCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
    hidden: !editColumnVisible.value,
  },
  {
    title: "Login",
    field: "UserName",
    columnMenu: "columnMenu",
  },
  {
    title: "Email",
    field: "Email",
    columnMenu: "columnMenu",
  },
  {
    title: "Roles",
    field: "RolesAsString",
    columnMenu: "columnMenu",
  },
  {
    title: "Status",
    field: "CognitoStatus",
    columnMenu: "columnMenu",
  },
  {
    title: "All Plants",
    field: "AllPlants",
    cell: "AllPlantsCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
  },
  {
    cell: "DeleteCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
    hidden: !deleteColumnVisible.value,
  },
]);

let pageSizes = [10, 20, 25, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("usersChanged", { skip: 0, take: take.value, filterEvent: e });
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("usersChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) columns.value = columnsProps;
  console.debug("UserGrid.vue, onColumnSubmit", columnsProps);
  emit("columnssubmit", columnsProps);
};

const openEditModal = function (user: MemberShipUserPlus) {
  emit("editClicked", user);
};

const openDeleteModal = function (user: MemberShipUserPlus) {
  emit("deleteClicked", user);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  emit("columnreorder", e);
};

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.k-grid {
  .open-edit {
    cursor: pointer;
    width: 1.25rem;
    height: 1.25rem;

    &:hover {
      color: #007bff;
    }
  }

  .open-delete {
    cursor: pointer;
    width: 1.25rem;
    height: 1.25rem;
    color: #dc3545;

    &:hover {
      color: unset;
    }
  }

  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}
</style>
